import React, { ReactNode, useState } from 'react';
import { Element } from './Collapse.styles';
import TransitionWrapper from './TransitionWrapper';
import Transition, { TransitionStatus } from 'react-transition-group/Transition';

export interface CollapseProps {
  open: boolean;
  className?: string;
  children?: ReactNode;
}

const transitionStatusToClass = {
  entering: 'collapsing',
  entered: 'show',
  exiting: 'collapsing',
  exited: 'collapsed',
  unmounted: 'unmounted',
};
const TRANSITION_TIMEOUT = 2000;

function getTransitionStatus(status: keyof typeof transitionStatusToClass): string {
  return transitionStatusToClass[status] || 'collapse';
}

function getDimension(node: Element) {
  return node.scrollHeight;
}

const Collapse = React.forwardRef<Transition<any>, CollapseProps>(function Collapse({ open, children, ...props }, ref) {
  const [dimension, setDimension] = useState<number | null>(null);

  const onEntering = (node: Element) => {
    setDimension(getDimension(node));
  };
  const onEntered = (node: Element) => {
    setDimension(null);
  };
  const onExit = (node: Element) => {
    setDimension(getDimension(node));
  };
  const onExiting = (node: Element) => {
    setDimension(0);
  };
  const onExited = (node: Element) => {
    setDimension(null);
  };
  return (
    <TransitionWrapper
      ref={ref}
      {...props}
      in={open}
      onEntering={onEntering}
      onEntered={onEntered}
      onExit={onExit}
      onExiting={onExiting}
      onExited={onExited}
      timeout={TRANSITION_TIMEOUT}
      childRef={(children as any).ref}
    >
      {(status: TransitionStatus, innerProps: Record<string, unknown>) => {
        let style = dimension === null ? null : { height: dimension };
        return (
          <Element style={{ ...style }} status={getTransitionStatus(status)} {...innerProps}>
            {children}
          </Element>
        );
      }}
    </TransitionWrapper>
  );
});

export default Collapse;
